import classNames from "classnames";
import { Button, CardContent } from "@material-ui/core";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { SocialMedia } from "../../service/data";
import { PlayArrow } from "@material-ui/icons";
import { isMediaTall } from "./mediaHeight";
import { useTranslate } from "../../service/translation";

interface SocialCardMediaProps {
    className: string;
    classes: {
        audio: string;
        image: string;
        video: string;
        mediaClip: string;
        mediaFade: string;
        mediaToggle: string;
    };
    media?: SocialMedia;
    title: string;
    source?: string;
    /**
     * Clip an over-tall image and offer to reveal it. Off for the single-post view, where somebody
     * has asked for one post on purpose and should get the whole picture.
     */
    cap?: boolean;
}

const SocialCardMedia: FunctionComponent<SocialCardMediaProps> = (props: SocialCardMediaProps) => {
    const { className, classes, media, title, source, cap = true } = props;
    const translate = useTranslate();
    const [expanded, setExpanded] = useState(false);
    const [tall, setTall] = useState(false);
    const holder = useRef<HTMLDivElement | null>(null);
    const img = useRef<HTMLImageElement | null>(null);

    /**
     * Whether this image overflows the cap can only be answered from its natural dimensions, so it
     * has to be measured rather than styled - see mediaHeight.
     *
     * Called from onLoad *and* on mount, because onLoad alone misses the common case: an image
     * already in the browser cache can finish before React attaches the handler, and then no load
     * event ever arrives. `complete` is how to tell, and it was the reason the reveal button did not
     * appear on a warm page.
     */
    const measure = useCallback(() => {
        const image = img.current;
        if (! cap || ! image || ! image.complete) {
            return;
        }
        const width = holder.current?.clientWidth || image.clientWidth;
        setTall(isMediaTall(image.naturalWidth, image.naturalHeight, width));
    }, [cap]);

    useEffect(measure, [measure, media?.src]);

    if (! media) {
        return null;
    }

    const { type, src } = media;

    if (type !== "photo" && type !== "video") {
        // A media type we cannot render is a card without a picture, not a blank page: throwing here
        // took the whole card down, and a second source means new types arrive without a release.
        console.warn(`Unsupported media type "${type}", rendering the post without it`);
        return null;
    }

    const clipped = cap && tall && ! expanded;

    // No height attribute: the stylesheet sets `height: auto !important`, so the 200 that used to be
    // here never applied and only looked as though it did.
    const picture = (
        <img
            ref={img}
            alt={title}
            className={classNames(className, classes.image)}
            src={src}
            width={"100%"}
            onLoad={measure}
        />
    );

    return (
        <CardContent>
            <div ref={holder} className={clipped ? classes.mediaClip : undefined}>
                {type === "video"
                    ? (
                        <a href={source} target="_blank" className="" rel="noopener noreferrer">
                            {picture}
                            <PlayArrow />
                        </a>
                    )
                    : picture}
                {clipped && <div className={classes.mediaFade} />}
            </div>
            {cap && tall && (
                <Button
                    className={classes.mediaToggle}
                    size="small"
                    onClick={() => setExpanded(! expanded)}
                >
                    {translate(expanded
                        ? "newshub.action.showLessImage"
                        : "newshub.action.showMoreImage")}
                </Button>
            )}
        </CardContent>
    );
};

export default SocialCardMedia;
